import { useFormDirty } from "../../../../../../shared/frontend/forms/useFormDirty";
/**
 * Wiederverwendbare Komponente „Vertrag kündigen“.
 * Kann aus dem Personenbereich, Aufgaben oder anderen Kontexten genutzt werden.
 */
import { Alert, App, Button, Form, Input, Select, Typography } from "../../../../../../shared/frontend/ui";
import { DetailField, DetailList } from "../../../../../../shared/frontend/components/DetailList";
import { DatePicker } from "../../../../../../shared/frontend/components/ControlledPickers";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../app/auth/useAuth";
import { Permissions } from "../../app/constants/permissions";
import { spacing } from "../../app/design-system";
import { useI18n } from "../../app/i18n/useI18n";
import { useDateInputFormats } from "../../app/units/DateInputFormatContext";
import { formatDateOnly, formatDateTimeNoSeconds, formatMoney, formatNumber } from "../../app/units/format";
import { loadUnits } from "../../app/units/unitsStorage";
import {
  ConfirmationDeliverySection,
  deliveryChannelToSendFlags,
  loadStoredDeliveryChannel,
  type DeliveryChannel
} from "../communication/DeliveryChannelSection";
import type { SendDocumentReviewRequest } from "../communication/SendDocumentReviewWorkspace";
import { FormDrawer } from "../FormDrawer";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../app/utils/isoDate";

/** Vorbelegung des Kündigungswirkungsdatums, wenn nichts Genaueres bekannt ist. */
const DEFAULT_NOTICE_DAYS = 30;
const WARNING_DURATION_SECONDS = 8;

export type ContractPackageForCancel = {
  id: string;
  studioId?: string | null;
  /** Optional: Anzeigename (Tarif/Plan) – wird z. B. im Aufgaben-Kontext angezeigt. */
  planName?: string | null;
  endDate?: string | null;
  cancellationEffectiveDate?: string | null;
  status?: number | null;
};

export type CancelContractDrawerProps = {
  open: boolean;
  onClose: () => void;
  contractPackage: ContractPackageForCancel | null;
  personId: string;
  person?: { id: string; name?: string | null; email?: string | null; homeStudioId?: string | null } | null;
  /** Wenn aus Aufgaben/App geöffnet: ID der Member-App-Anfrage (wird beim Speichern mitgeschickt). */
  memberAppRequestId?: string | null;
  /** Vorbelegung Kündigungsdatum (z. B. aus App-Anfrage). */
  requestedEffectiveDate?: string | null;
  requestReceivedAt?: string | null;
  requestNote?: string | null;
  requestReasonId?: string | null;
  inboundEmail?: { id: string; updatedAt: string } | null;
  requestContext?: React.ReactNode;
  /**
   * Aufruf ohne festes Paket (Aktenaktion bei mehreren kündbaren Paketen): nur diese Pakete sind
   * wählbar, und keines ist vorausgewählt — das richtige wählt man bewusst.
   */
  choosablePackageIds?: string[] | null;
  /** Nach erfolgreicher Kündigung. */
  onSuccess?: () => void;
  /** Öffnet die Versandbearbeitung als Arbeitsfläche im Seiteninhalt der aufrufenden Seite. */
  onOpenDocumentReview: (request: SendDocumentReviewRequest) => void;
};

type FormValues = {
  contractPackageId?: string;
  cancellationRequestedDate?: Dayjs;
  membershipEndDate?: Dayjs;
  cancellationReasonId?: string;
  note?: string;
  sendConfirmation?: boolean;
  /** Ob das Schreiben vor dem Versand angesehen und im Wortlaut geaendert wird. */
  editBeforeSend?: boolean;
  /** Textvariante des Schreibens (Bestätigung, Ablehnungsgrund); leer = Grundfall. */
  variantId?: string;
  deliveryChannel?: DeliveryChannel;
};

type CancellationPreview = {
  cancellationRequestedDate?: string | null;
  cancellationEffectiveDate?: string | null;
  membershipEndDate?: string | null;
  cancellationBlockedByPromotion?: boolean;
  remainingContributionCount?: number;
  remainingContributionAmount?: number;
  droppedContributionCount?: number;
  droppedContributionAmount?: number;
  remainingContributionUnitAmount?: number | null;
  droppedContributionUnitAmount?: number | null;
};

/** Neuer Einzelkanal-Key; die alten Mehrkanal-Keys (…DeliveryChannels) verfallen bewusst. */
const DELIVERY_CHANNEL_STORAGE_KEY = "m1member.cancelContractDeliveryChannel";
const CONTRACT_STATUS_DRAFT = 1;
type AppCancellationSource = { note?: string | null; createdAt: string; requestedEffectiveDate?: string | null };
type ConfirmationRequest = { contractPackageId: string; variantId: string | null; sendEmail: boolean };

function resolvePackageMembershipEndDate(
  pkg: ContractPackageForCancel,
  contracts: Array<{ sourceContractPackageId?: string; isPrimary?: boolean; endDate?: string }> | null | undefined
) {
  const packageContracts = (contracts || []).filter((c) => c.sourceContractPackageId === pkg.id);
  const primary = packageContracts.find((c) => c.isPrimary === true);
  if (primary?.endDate) return dayjs(String(primary.endDate).slice(0, ISO_DATE_LENGTH));
  if (pkg.endDate) return dayjs(pkg.endDate);
  if (pkg.cancellationEffectiveDate) return dayjs(pkg.cancellationEffectiveDate);
  return dayjs().add(DEFAULT_NOTICE_DAYS, "days");
}

export function CancelContractDrawer({
  open,
  onClose,
  contractPackage,
  personId,
  person,
  memberAppRequestId,
  requestedEffectiveDate,
  requestReceivedAt,
  requestNote,
  requestReasonId,
  inboundEmail,
  requestContext,
  choosablePackageIds,
  onSuccess,
  onOpenDocumentReview,
}: CancelContractDrawerProps) {
  const auth = useAuth();
  const canCustomizeCancellationDates = auth.hasAnyPermission(Permissions.ContractsCancelCustomize);
  const { text } = useI18n();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const inboundEmailId = inboundEmail?.id;
  const dateInputFormats = useDateInputFormats();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);

  const [form] = Form.useForm<FormValues>();
  const { dirty, initialize, resetDirty } = useFormDirty(form);
  const [saving, setSaving] = React.useState(false);
  const [appSource, setAppSource] = React.useState<AppCancellationSource | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = React.useState<ConfirmationRequest | null>(null);
  const [cancellationReasons, setCancellationReasons] = React.useState<Array<{ id: string; name: string }>>([]);
  const [confirmationVariants, setConfirmationVariants] = React.useState<Array<{ id: string; name: string }>>([]);
  const [letterSendingIntegrations, setLetterSendingIntegrations] = React.useState<
    Array<{ id: string; integrationType: string; displayName: string }>
  >([]);
  const [studios, setStudios] = React.useState<Array<{ id: string }>>([]);
  const [contractsList, setContractsList] = React.useState<ContractPackageForCancel[]>([]);
  const [allPersonContracts, setAllPersonContracts] = React.useState<Array<{ sourceContractPackageId?: string; isPrimary?: boolean; endDate?: string }>>([]);
  const [lookupLoadFailed, setLookupLoadFailed] = React.useState(false);
  // Fünf Abrufe plus Vorschau beim Öffnen. Ohne dieses Kennzeichen stand der Drawer offen, während
  // sich Kündigungsgründe, Vertragspakete und Versandwege einzeln nachfüllten — eine Auswahlliste
  // war in der Lücke anklickbar und leer.
  const [lookupsLoading, setLookupsLoading] = React.useState(false);
  const [cancellationBlockedByPromotion, setCancellationBlockedByPromotion] = React.useState(false);
  const watchedContractPackageId = Form.useWatch("contractPackageId", form);
  const watchedRequestedDate = Form.useWatch("cancellationRequestedDate", form);
  const watchedMembershipEndDate = Form.useWatch("membershipEndDate", form);
  const requestedDateKey = watchedRequestedDate?.isValid() ? watchedRequestedDate.format("YYYY-MM-DD") : null;
  const membershipEndDateKey = watchedMembershipEndDate?.isValid() ? watchedMembershipEndDate.format("YYYY-MM-DD") : null;

  /**
   * Die Varianten des Schreibens — Bestätigung und die angelegten Ablehnungsfälle. Ohne Auswahl
   * gilt der Grundfall; eine Variante ist eine andere Formulierung, kein anderes Dokument.
   */
  const loadConfirmationVariants = React.useCallback(async (studioId: string | null | undefined) => {
    const query = new URLSearchParams({ language: "de" });
    if (studioId) query.set("studioId", studioId);
    const variants = await auth.api.get<Array<{ id: string; name: string }>>(
      `/api/v1/system-documents/cancellation-confirmation/variants?${query.toString()}`
    );
    setConfirmationVariants(variants || []);
    const current = form.getFieldValue("variantId");
    if (current && !(variants || []).some((variant) => variant.id === current)) {
      form.setFieldValue("variantId", null);
    }
  }, [auth.api, form]);

  const resolveMembershipEndDate = React.useCallback((pkg: ContractPackageForCancel) => {
    return resolvePackageMembershipEndDate(pkg, allPersonContracts);
  }, [allPersonContracts]);

  const loadCancellationPreview = React.useCallback(async (
    contractPackageId: string,
    cancellationRequestedDate?: Dayjs | string | null,
    membershipEndDate?: Dayjs | string | null
  ): Promise<CancellationPreview | null> => {
    const query = new URLSearchParams();
    const requestedDateIso = typeof cancellationRequestedDate === "string"
      ? cancellationRequestedDate.slice(0, ISO_DATE_LENGTH)
      : cancellationRequestedDate?.isValid()
        ? cancellationRequestedDate.format("YYYY-MM-DD")
        : null;
    const membershipEndDateIso = typeof membershipEndDate === "string"
      ? membershipEndDate.slice(0, ISO_DATE_LENGTH)
      : membershipEndDate?.isValid()
        ? membershipEndDate.format("YYYY-MM-DD")
        : null;
    if (requestedDateIso) query.set("cancellationRequestedDate", requestedDateIso);
    if (membershipEndDateIso) query.set("membershipEndDate", membershipEndDateIso);
    if (memberAppRequestId) query.set("memberAppRequestId", memberAppRequestId);
    if (inboundEmailId) query.set("inboundEmailId", inboundEmailId);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return await auth.api.get<CancellationPreview>(
      `/api/v1/contract-packages/${encodeURIComponent(contractPackageId)}/cancellation-preview${suffix}`,
      { suppressGlobalErrorToast: true }
    ).catch(() => null);
  }, [auth.api, memberAppRequestId, inboundEmailId]);

  // Die Folgen gehören immer zum sichtbaren Termin, auch bei manueller Änderung oder App-Vorbelegung.
  const previewEnabled = open && !lookupsLoading && !!watchedContractPackageId && !!requestedDateKey && !!membershipEndDateKey;
  const consequenceQuery = useQuery({
    queryKey: ["contract-cancellation-preview", personId, watchedContractPackageId, requestedDateKey, membershipEndDateKey, memberAppRequestId, inboundEmailId],
    enabled: previewEnabled,
    queryFn: async () => {
      const preview = await loadCancellationPreview(watchedContractPackageId!, requestedDateKey, membershipEndDateKey);
      if (!preview) throw new Error("The cancellation preview could not be loaded.");
      return preview;
    },
    retry: false,
  });
  const consequences = previewEnabled ? consequenceQuery.data : undefined;
  const previewLoading = previewEnabled && consequenceQuery.isPending;
  const previewFailed = previewEnabled && consequenceQuery.isError;

  const formatContributionSummary = (count: number | undefined, amount: number | undefined, unitAmount: number | null | undefined, emptyKey: string) => {
    if (count == null || amount == null) return "—";
    if (count === 0) return text(emptyKey).replace("{0}", formatMoney(amount, units));
    if (unitAmount != null) {
      return text("Cancellation equal contribution summary")
        .replace("{0}", formatNumber(count, units))
        .replace("{1}", formatMoney(unitAmount, units))
        .replace("{2}", formatMoney(amount, units));
    }
    return text("Cancellation mixed contribution summary")
      .replace("{0}", formatNumber(count, units))
      .replace("{1}", formatMoney(amount, units));
  };

  // Studio-Kontext des gewählten Pakets — für die E-Mail-Verfügbarkeit in der Versand-Sektion.
  const selectedStudioId = React.useMemo(() => {
    const selectedPackage =
      contractsList.find((p) => p.id === watchedContractPackageId) ||
      (contractPackage?.id === watchedContractPackageId ? contractPackage : null);
    return selectedPackage?.studioId || studios[0]?.id || null;
  }, [contractPackage, contractsList, studios, watchedContractPackageId]);

  // Daten laden wenn Drawer öffnet
  React.useEffect(() => {
    if (!open || !contractPackage) return;

    let alive = true;
    setCancellationReasons([]);
    setStudios([]);
    setContractsList([]);
    setAllPersonContracts([]);
    setLetterSendingIntegrations([]);
    setConfirmationVariants([]);
    setLookupLoadFailed(false);
    setCancellationBlockedByPromotion(false);
    setLookupsLoading(true);
    (async () => {
      try {
        const [reasons, studiosList, integrations, packageRows, allContracts, appRequest] = await Promise.all([
          auth.api.get<Array<{ id: string; name: string }>>("/api/v1/cancellation-reasons"),
          auth.api.get<Array<{ id: string }>>("/api/v1/studios"),
          auth.api.get<Array<{ id: string; integrationType: string; displayName: string }>>("/api/v1/integrations/by-capability/LetterSending"),
          auth.api.get<ContractPackageForCancel[]>(`/api/v1/contract-packages?personId=${encodeURIComponent(personId)}`),
          auth.api.get<Array<{ sourceContractPackageId?: string; isPrimary?: boolean; endDate?: string }>>(
            `/api/v1/contracts?personId=${encodeURIComponent(personId)}`
          ),
          memberAppRequestId ? auth.api.get<AppCancellationSource>(`/api/v1/studio-app-requests/${encodeURIComponent(memberAppRequestId)}`) : Promise.resolve(null),
        ]);
        if (!alive) return;
        setAppSource(appRequest);
        setCancellationReasons(reasons || []);
        setStudios(studiosList || []);
        setLetterSendingIntegrations(integrations || []);
        setAllPersonContracts(allContracts || []);
        const packages = (packageRows || []).filter((p) => Number(p.status) !== CONTRACT_STATUS_DRAFT);
        const selectedPackage = packages.find((p) => p.id === contractPackage.id) || contractPackage;
        const selectablePackages = packages.some((p) => p.id === selectedPackage.id)
          ? packages
          : [selectedPackage, ...packages];
        const mustChoose = !!choosablePackageIds && choosablePackageIds.length > 1;
        setContractsList(choosablePackageIds?.length ? selectablePackages.filter((p) => choosablePackageIds.includes(p.id)) : selectablePackages);
        form.setFieldValue("contractPackageId", mustChoose ? undefined : selectedPackage.id);

        await loadConfirmationVariants(selectedPackage.studioId || studiosList?.[0]?.id);
        if (!alive) return;

        const fromRequest = requestReceivedAt ?? appRequest?.createdAt ?? null;
        const preview = await loadCancellationPreview(selectedPackage.id, fromRequest);
        if (!alive) return;
        setCancellationBlockedByPromotion(preview?.cancellationBlockedByPromotion === true);

        let defaultRequestedDate = preview?.cancellationRequestedDate
          ? dayjs(String(preview.cancellationRequestedDate).slice(0, ISO_DATE_LENGTH))
          : dayjs();
        let defaultMembershipEndDate = preview?.membershipEndDate || preview?.cancellationEffectiveDate
          ? dayjs(String(preview.membershipEndDate || preview.cancellationEffectiveDate).slice(0, ISO_DATE_LENGTH))
          : dayjs().add(DEFAULT_NOTICE_DAYS, "days");
        if (selectedPackage.cancellationEffectiveDate) {
          defaultMembershipEndDate = dayjs(selectedPackage.cancellationEffectiveDate);
        }
        if (fromRequest) {
          defaultRequestedDate = dayjs(String(fromRequest).slice(0, ISO_DATE_LENGTH));
        }
        if (canCustomizeCancellationDates && requestedEffectiveDate) {
          defaultMembershipEndDate = dayjs(requestedEffectiveDate.slice(0, ISO_DATE_LENGTH));
        }
        if (!defaultRequestedDate.isValid()) defaultRequestedDate = dayjs();
        if (!defaultMembershipEndDate.isValid()) defaultMembershipEndDate = resolvePackageMembershipEndDate(selectedPackage, allContracts);

        const storedChannel = loadStoredDeliveryChannel(DELIVERY_CHANNEL_STORAGE_KEY, auth.info?.accountId);
        const hasEmail = !!person?.email;
        const defaultChannel: DeliveryChannel =
          storedChannel && (storedChannel !== "email" || hasEmail) ? storedChannel : hasEmail ? "email" : "print";

        initialize({
          contractPackageId: mustChoose ? undefined : selectedPackage.id,
          cancellationRequestedDate: defaultRequestedDate,
          // Ohne gewähltes Paket gibt es noch kein Mitgliedschaftsende; die Wahl setzt es.
          membershipEndDate: mustChoose ? undefined : defaultMembershipEndDate,
          note: requestNote ?? appRequest?.note ?? null,
          cancellationReasonId: requestReasonId ?? null,
          sendConfirmation: true,
          deliveryChannel: defaultChannel,
        });
      } catch {
        if (alive) setLookupLoadFailed(true);
      } finally {
        if (alive) setLookupsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [
    open,
    contractPackage?.id,
    contractPackage?.studioId,
    contractPackage?.endDate,
    contractPackage?.cancellationEffectiveDate,
    contractPackage?.planName,
    personId,
    requestedEffectiveDate,
    requestReceivedAt,
    requestNote,
    requestReasonId,
    memberAppRequestId,
    choosablePackageIds,
    auth.api,
    form,
    initialize,
    canCustomizeCancellationDates,
    loadConfirmationVariants,
    loadCancellationPreview
  ]);

  const handleClose = () => {
    setPendingConfirmation(null);
    form.resetFields();
    onClose();
  };

  const sendConfirmationRequest = (confirmation: ConfirmationRequest) => auth.api.post(
    "/api/v1/system-documents/cancellation-confirmation/send", {
      referenceId: confirmation.contractPackageId, variantId: confirmation.variantId,
      sendEmail: confirmation.sendEmail, language: "de",
    });

  const handleSubmit = async () => {
    if (!contractPackage) return;
    const contractPackageId = String(form.getFieldValue("contractPackageId") || "").trim();
    if (!contractPackageId) {
      feedback.error(text("Please select"));
      return;
    }
    const selectedPackage = contractsList.find((p) => p.id === contractPackageId) || (contractPackage.id === contractPackageId ? contractPackage : null);
    if (!selectedPackage) {
      feedback.error(text("Please select"));
      return;
    }

    const v = await form.validateFields().catch(() => null);
    if (!v) return;
    const requestedDateIso = v.cancellationRequestedDate?.isValid() ? v.cancellationRequestedDate.format("YYYY-MM-DD") : null;
    const membershipEndDateIso = v.membershipEndDate?.isValid() ? v.membershipEndDate.format("YYYY-MM-DD") : null;

    const sendConfirmation = v.sendConfirmation !== false;
    const deliveryChannel = (form.getFieldValue("deliveryChannel") ?? null) as DeliveryChannel | null;
    if (sendConfirmation && !deliveryChannel) {
      feedback.warning(text("Please select a delivery channel."));
      return;
    }
    const flags = sendConfirmation
      ? deliveryChannelToSendFlags(deliveryChannel)
      : deliveryChannelToSendFlags(null);
    const sendEmail = flags.sendEmail;

    setSaving(true);
    try {
      await auth.api.post(`/api/v1/contract-packages/${encodeURIComponent(contractPackageId)}/cancel`, {
        cancellationRequestedDate: requestedDateIso || undefined,
        cancellationEffectiveDate: membershipEndDateIso || undefined,
        membershipEndDate: membershipEndDateIso || undefined,
        cancellationReasonId: v.cancellationReasonId || null,
        note: v.note || null,
        processedByUserId: auth.info?.accountId || null,
        processedByUserName: auth.info?.displayName || null,
        terminateServices: true,
        terminateWallets: true,
        memberAppRequestId: memberAppRequestId || undefined,
        inboundEmailId: inboundEmail?.id,
        inboundEmailUpdatedAt: inboundEmail?.updatedAt,
        contributionHandling: "adjust",
      });

      resetDirty();
      feedback.success(text("Contract cancelled."));

      // Vor dem Versand bearbeiten heisst: erst kuendigen, dann das Schreiben ansehen. Vorher
      // traegt der Vertrag die Kuendigungsdaten noch nicht, und die Ansicht zeigte einen Stand,
      // den es nicht gibt.
      if (sendConfirmation && person && v.editBeforeSend === true) {
        handleClose();
        onOpenDocumentReview({
          kind: "cancellation-confirmation",
          referenceId: contractPackageId,
          variantId: v.variantId || null,
          sendEmail,
          title: text("Cancellation confirmation"),
          onCancel: () => {
            modal.info({
              title: text("Confirmation not sent"),
              content: text("Contract cancelled without confirmation hint"),
            });
            onSuccess?.();
          },
          onSent: (result) => {
            result.warnings?.forEach((warning) => feedback.warning(warning, WARNING_DURATION_SECONDS));
            feedback.success(text("Confirmation sent."));
            onSuccess?.();
          },
        });
        return;
      }

      // Die Bestätigung entsteht aus den Vertragsdaten und den gepflegten Texten. Der Versand
      // läuft über dieselbe Kette wie sonst — Ablage, Postausgang und Testmodus bleiben unberührt.
      if (sendConfirmation && person) {
        const confirmation = { contractPackageId, variantId: v.variantId || null, sendEmail };
        try {
          await sendConfirmationRequest(confirmation);
        } catch {
          // Die Kündigung bleibt abgeschlossen. Nur den fehlgeschlagenen Versand wiederholen,
          // niemals den bereits gespeicherten Kündigungsvorgang erneut absenden.
          setPendingConfirmation(confirmation);
          onSuccess?.();
          return;
        }
      }

      handleClose();
      onSuccess?.();
    } catch {
      // Central API toast
    } finally {
      setSaving(false);
    }
  };

  if (!contractPackage) return null;

  const handleContractPackageChange = (value: string) => {
    const selected = contractsList.find((p) => p.id === value) || (contractPackage.id === value ? contractPackage : null);
    if (!selected) return;

    void (async () => {
      const requestedDate = form.getFieldValue("cancellationRequestedDate");
      const preview = await loadCancellationPreview(selected.id, requestedDate);
      if (form.getFieldValue("contractPackageId") !== selected.id) return;
      setCancellationBlockedByPromotion(preview?.cancellationBlockedByPromotion === true);
      let membershipEndDate = preview?.membershipEndDate || preview?.cancellationEffectiveDate
        ? dayjs(String(preview.membershipEndDate || preview.cancellationEffectiveDate).slice(0, ISO_DATE_LENGTH))
        : resolveMembershipEndDate(selected);
      if (!membershipEndDate.isValid()) membershipEndDate = resolveMembershipEndDate(selected);
      form.setFieldsValue({ membershipEndDate });
    })();
    form.setFieldValue("variantId", null);
    void loadConfirmationVariants(selected.studioId || studios[0]?.id);
  };

  const handleCancellationRequestedDateChange = (date: Dayjs | null) => {
    if (!canCustomizeCancellationDates || !date?.isValid()) return;
    const contractPackageId = String(form.getFieldValue("contractPackageId") || "").trim();
    const selected = contractsList.find((p) => p.id === contractPackageId) || (contractPackage.id === contractPackageId ? contractPackage : null);
    if (!selected) return;

    void (async () => {
      const preview = await loadCancellationPreview(selected.id, date);
      if (form.getFieldValue("contractPackageId") !== selected.id) return;
      const currentRequestedDate = form.getFieldValue("cancellationRequestedDate");
      if (!currentRequestedDate?.isSame(date, "day")) return;
      setCancellationBlockedByPromotion(preview?.cancellationBlockedByPromotion === true);

      let membershipEndDate = preview?.membershipEndDate || preview?.cancellationEffectiveDate
        ? dayjs(String(preview.membershipEndDate || preview.cancellationEffectiveDate).slice(0, ISO_DATE_LENGTH))
        : resolveMembershipEndDate(selected);
      if (!membershipEndDate.isValid()) membershipEndDate = resolveMembershipEndDate(selected);
      form.setFieldsValue({ membershipEndDate });
    })();
  };

  return (
    <FormDrawer
      title="Cancel contract"
      dirty={dirty}
      open={open}
      onCancel={handleClose}
      formId="cancel-contract-form"
      submitting={saving}
      contentLoading={lookupsLoading}
      submitText="Terminate"
      dangerous
      hideSubmit={lookupLoadFailed || previewLoading || previewFailed || cancellationBlockedByPromotion || !!pendingConfirmation}
    >
      {lookupLoadFailed ? (
        <Alert type="error" showIcon message={text("Error loading data")} style={{ marginBottom: spacing.lg }} />
      ) : null}
      {previewFailed ? (
        <Alert type="error" showIcon message={text("The cancellation preview could not be loaded.")} style={{ marginBottom: spacing.lg }} />
      ) : null}
      {cancellationBlockedByPromotion ? (
        <Alert
          type="warning"
          showIcon
          message={text("Cancellation blocked during free period")}
          style={{ marginBottom: spacing.lg }}
        />
      ) : null}
      {pendingConfirmation ? <>
        <Alert type="error" message={text("Confirmation not sent")}
          description={text("Contract cancelled without confirmation hint")} style={{ marginBottom: spacing.md }} />
        <Button loading={saving} onClick={async () => {
          setSaving(true);
          try {
            await sendConfirmationRequest(pendingConfirmation);
            feedback.success(text("Confirmation sent."));
            handleClose();
            onSuccess?.();
          } catch {
            // API-Fehler wird zentral angezeigt; die konkrete Versandaktion bleibt verfügbar.
          } finally { setSaving(false); }
        }}>{text("Send confirmation")}</Button>
      </> : <Form id="cancel-contract-form" form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
        {requestContext}
        {appSource ? <DetailList>
          <DetailField label={text("Cancellation request date")}>{formatDateTimeNoSeconds(appSource.createdAt, units)}</DetailField>
          <DetailField label={text("Requested cancellation date")}>{appSource.requestedEffectiveDate
            ? formatDateOnly(appSource.requestedEffectiveDate, units) : "—"}</DetailField>
          <DetailField label={text("Original request note")}>{appSource.note || "—"}</DetailField>
        </DetailList> : null}
        <Form.Item
          name="contractPackageId"
          label={text("Contract package")}
          rules={[{ required: true, message: text("Please select") }]}
        >
          <Select
            showSearch
            disabled={!!memberAppRequestId || !!inboundEmail}
            optionFilterProp="label"
            placeholder={text("Select contract package")}
            options={contractsList.map((c) => ({ value: c.id, label: c.planName || c.id }))}
            onChange={handleContractPackageChange}
          />
        </Form.Item>
        {!canCustomizeCancellationDates ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.md }}>
            {text("Cancellation dates follow system model help")}
          </Typography.Paragraph>
        ) : null}
        <Form.Item
          name="cancellationRequestedDate"
          label={text("Cancellation request date")}
          rules={[{ required: true, message: text("Cancellation request date is required") }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format={dateInputFormats}
            allowClear={false}
            disabled={!canCustomizeCancellationDates}
            onChange={handleCancellationRequestedDateChange}
          />
        </Form.Item>
        <Form.Item
          name="membershipEndDate"
          label={text("Membership end date")}
          rules={[{ required: true, message: text("Membership end date is required") }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format={dateInputFormats}
            allowClear={false}
            disabled={!canCustomizeCancellationDates}
          />
        </Form.Item>
        {watchedContractPackageId && membershipEndDateKey ? (
          <div style={{ marginBottom: spacing.lg }}>
          <DetailList>
            <DetailField label={text("Remaining contributions")}>
              {consequences
                ? formatContributionSummary(consequences.remainingContributionCount, consequences.remainingContributionAmount,
                  consequences.remainingContributionUnitAmount, "Cancellation no remaining contributions")
                : "—"}
            </DetailField>
            <DetailField label={text("Dropped contributions")}>
              {consequences ? formatContributionSummary(consequences.droppedContributionCount, consequences.droppedContributionAmount,
                consequences.droppedContributionUnitAmount, "Cancellation no dropped contributions") : "—"}
            </DetailField>
          </DetailList>
          </div>
        ) : null}
        <Form.Item name="cancellationReasonId" label={text("Cancellation reason")}>
          <Select
            options={cancellationReasons.map((r) => ({ value: r.id, label: r.name }))}
            placeholder={text("Select cancellation reason…")}
            allowClear
          />
        </Form.Item>
        <Form.Item name="note" label={text("Note")}>
          <Input.TextArea rows={3} placeholder={text("Optional")} />
        </Form.Item>

        <ConfirmationDeliverySection
          heading={
            <Typography.Paragraph type="secondary" strong style={{ marginTop: spacing.lg, marginBottom: spacing.sm }}>
              {text("Confirmation")}
            </Typography.Paragraph>
          }
          open={open}
          form={form}
          studioId={selectedStudioId}
          personEmail={person?.email}
          integrations={letterSendingIntegrations}
          variantOptions={confirmationVariants.map((variant) => ({ value: variant.id, label: variant.name }))}
          variantLabel={text("Cancellation confirmation")}
          storageKey={DELIVERY_CHANNEL_STORAGE_KEY}
          showEditBeforeSend
        />
      </Form>}
    </FormDrawer>
  );
}
